import { Link, useNavigate } from 'react-router-dom';
import { Colors, IMaterial, IPodcasts } from '../../../../models/disciplines';
import { IExercise } from '../../../../models/exercise';
import { Typography } from '../../../molecules/Typography';
import InnerHTMLContainer from '../../../molecules/InnerHTMLContainer';
import ContentCard from '../../ContentCard';
import BrainLoadingWrapper from '../../../molecules/BrainLoading';
import { formatTime } from '../../../../commom/formatTime';
import formatFileCategory from '../../../../commom/formatFileCategory';
import { useFreemiumLock } from '../../FreemiumLockModal/useFreemiumLock';

interface RelatedContentSectionProps {
  title: string;
  linkTo: string;
  showLink: boolean;
  children: React.ReactNode;
}

function RelatedContentSection({
  title,
  linkTo,
  showLink,
  children,
}: RelatedContentSectionProps) {
  return (
    <div className='xs:basis-full lg:basis-1/3'>
      <div className='mb-4 flex flex-row items-end justify-between'>
        <Typography
          type='titleSection'
          size='h5'
          className='text-mono-color-dark'
        >
          {title}
        </Typography>
        {showLink && (
          <Link to={linkTo} className='text-sm text-color-brand-primary'>
            Ver mais
          </Link>
        )}
      </div>
      <div className='flex flex-col gap-md'>{children}</div>
    </div>
  );
}

interface RelatedContentProps {
  discipline: string;
  module?: string;
  data: any;
  isPending: boolean;
  onFavoriteCallback?: (data: any) => void;
  onDone?: (id: string, slug: string) => void;
}

export default function RelatedContent({
  discipline,
  module,
  data,
  isPending,
  onFavoriteCallback,
  onDone,
}: RelatedContentProps) {
  const navigate = useNavigate();
  const { isLocked, openLock } = useFreemiumLock();

  if (isPending) return <BrainLoadingWrapper />;

  return (
    <div>
      <div className='mb-8 mt-8 flex gap-xxxl xs:flex-col lg:flex-row'>
        <RelatedContentSection
          title='EXERCÍCIOS'
          linkTo={`/disciplinas/${discipline}/modulo/${module}/tipo/exercicio`}
          showLink={(data?.exercise?.length ?? 0) >= 2}
        >
          {data?.exercise.slice(0, 3).map((it: IExercise, i: number) => {
            const locked = isLocked(it);
            return (
            <ContentCard
              color={discipline as Colors}
              key={it.id || i}
              content='Exercício'
              favorite={it.favorite}
              icon='edit'
              action={() =>
                locked
                  ? openLock()
                  : navigate(
                      `/disciplinas/${discipline}/modulo/${module}/assunto/${it.subjects[0].slug}/exercicio/${it.slug}`
                    )
              }
              subject={it.subjects[0].title}
              id={it.id}
              slug={it.slug}
              done={it.done}
              hasCallback={!!onFavoriteCallback}
              callback={onFavoriteCallback}
              // "Marcar exercício como concluído" desabilitado (implementação futura).
              // O ícone permanece visível; nenhum `onDone` é passado, então o botão fica inerte.
              // Para reativar, passar: onDone={onDone}
              locked={locked}
            >
              <InnerHTMLContainer
                data={it.statement}
                type='contentCard'
                align='left'
                font='noto'
                className='text-sm text-mono-color-darkest transition delay-100 ease-in-out group-hover:text-card-color-default'
              />
            </ContentCard>
            );
          })}
        </RelatedContentSection>

        <RelatedContentSection
          title='MATERIAIS'
          linkTo={`/disciplinas/${discipline}/modulo/${module}/tipo/materiais`}
          showLink={data?.workbooks.length === 2}
        >
          {data?.workbooks.map((it: IMaterial, i: number) => {
            const locked = isLocked(it);
            return (
            <ContentCard
              color={discipline as Colors}
              key={it.id || i}
              title={it.title}
              done={it.done}
              favorite={it.favorite}
              icon='article'
              content='materiais'
              action={() =>
                locked
                  ? openLock()
                  : navigate(
                      `/disciplinas/${discipline}/modulo/${module}/assunto/${it.subjects[0].slug}/material/${it.slug}`
                    )
              }
              subject={it.subjects[0].title}
              id={it.id}
              slug={it.slug}
              hasCallback={!!onFavoriteCallback}
              callback={onFavoriteCallback}
              onDone={onDone}
              doneLoading={false}
              locked={locked}
            >
              <Typography
                size='small'
                className='transition delay-100 ease-in-out group-hover:text-card-color-default'
              >
                {formatFileCategory(it.file_category)}
              </Typography>
            </ContentCard>
            );
          })}
        </RelatedContentSection>

        <RelatedContentSection
          title='PODCASTS'
          linkTo={`/disciplinas/${discipline}/modulo/${module}/tipo/podcasts`}
          showLink={data?.podcast.length === 2}
        >
          {data?.podcast.map((it: IPodcasts, i: number) => {
            const locked = isLocked(it);
            return (
            <ContentCard
              color={discipline as Colors}
              key={it.id || i}
              title={it.title}
              done={it.done}
              favorite={it.favorite}
              icon='mic'
              content='podcast'
              action={() =>
                locked
                  ? openLock()
                  : navigate(
                      `/disciplinas/${discipline}/modulo/${module}/tipo/podcast`
                    )
              }
              subject={it.subjects[0].title}
              id={it.id}
              slug={it.slug}
              hasCallback={!!onFavoriteCallback}
              callback={onFavoriteCallback}
              onDone={onDone}
              doneLoading={false}
              locked={locked}
            >
              <Typography
                size='small'
                className='transition delay-100 ease-in-out group-hover:text-card-color-default'
              >
                {it.duration ? formatTime(it.duration) : ''}
              </Typography>
            </ContentCard>
            );
          })}
        </RelatedContentSection>
      </div>
    </div>
  );
}
